/* Progressive enhancement: content and horizontal scrolling work without JS. */
(() => {
  if (window.panchaHomeInitialized) return;
  window.panchaHomeInitialized = true;
  const cleanups = new WeakMap();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCarousel(root) {
    if (cleanups.has(root)) return;
    const list = root.querySelector('.pancha-home__ranking');
    const previous = root.querySelector('[data-pancha-prev]');
    const next = root.querySelector('[data-pancha-next]');
    if (!list || !previous || !next) return;
    const update = () => {
      const max = list.scrollWidth - list.clientWidth;
      previous.hidden = next.hidden = max <= 1;
      previous.disabled = list.scrollLeft <= 1;
      next.disabled = list.scrollLeft >= max - 1;
    };
    const step = direction => {
      const item = list.firstElementChild;
      if (!item) return;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      list.scrollBy({ left: direction * (item.getBoundingClientRect().width + gap), behavior: reducedMotion() ? 'instant' : 'smooth' });
    };
    const prevClick = () => step(-1);
    const nextClick = () => step(1);
    const keydown = event => {
      if (event.target !== list) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        step(event.key === 'ArrowLeft' ? -1 : 1);
      }
    };
    previous.addEventListener('click', prevClick);
    next.addEventListener('click', nextClick);
    list.addEventListener('scroll', update, { passive: true });
    list.addEventListener('keydown', keydown);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(list);
    window.addEventListener('resize', update);
    update();
    cleanups.set(root, () => {
      previous.removeEventListener('click', prevClick);
      next.removeEventListener('click', nextClick);
      list.removeEventListener('scroll', update);
      list.removeEventListener('keydown', keydown);
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
      cleanups.delete(root);
    });
  }

  function initTabs(root) {
    if (cleanups.has(root)) return;
    const tablist = root.querySelector('[data-pancha-tablist]');
    const tabs = [...root.querySelectorAll('[data-pancha-tab]')];
    const panels = [...root.querySelectorAll('[data-pancha-panel]')];
    if (!tablist || !tabs.length || tabs.length !== panels.length) return;
    tablist.setAttribute('role', 'tablist');
    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panels[index].id);
      panels[index].setAttribute('role', 'tabpanel');
      panels[index].setAttribute('aria-labelledby', tab.id);
      panels[index].tabIndex = 0;
    });
    const activate = (index, focus = false) => {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        panels[i].hidden = !active;
      });
      if (focus) {
        tabs[index].focus({ preventScroll: true });
        tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion() ? 'instant' : 'smooth' });
      }
    };
    const click = event => {
      const tab = event.target.closest('[data-pancha-tab]');
      const index = tabs.indexOf(tab);
      if (index < 0) return;
      event.preventDefault();
      activate(index);
    };
    const keydown = event => {
      const index = tabs.indexOf(event.target);
      if (index < 0) return;
      let target;
      if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = tabs.length - 1;
      if (event.key === ' ') target = index;
      if (target !== undefined) {
        event.preventDefault();
        activate(target, true);
      }
    };
    const selectBlock = event => {
      const index = panels.findIndex(panel => panel.dataset.panchaBlockId === event.detail?.blockId);
      if (index >= 0) activate(index);
    };
    const selectHash = () => {
      const index = panels.findIndex(panel => '#' + panel.id === window.location.hash);
      if (index >= 0) activate(index);
    };
    tablist.addEventListener('click', click);
    tablist.addEventListener('keydown', keydown);
    root.addEventListener('shopify:block:select', selectBlock);
    window.addEventListener('hashchange', selectHash);
    root.classList.add('pancha-home--tabs-ready');
    activate(0);
    selectHash();
    cleanups.set(root, () => {
      tablist.removeEventListener('click', click);
      tablist.removeEventListener('keydown', keydown);
      root.removeEventListener('shopify:block:select', selectBlock);
      window.removeEventListener('hashchange', selectHash);
      cleanups.delete(root);
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-pancha-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-pancha-tabs]').forEach(initTabs);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-pancha-carousel], [data-pancha-tabs]').forEach(root => cleanups.get(root)?.());
  });
})();
